// Geometry helpers for the DXF parser: OCS, affine transforms, flattening of
// bulges, arcs, ellipses and NURBS splines. Pure TS, no dependencies.

export type Pt = [number, number];
export type V3 = [number, number, number];

/** 2D affine matrix [a, b, c, d, e, f]: x' = a*x + c*y + e, y' = b*x + d*y + f */
export type M2 = [number, number, number, number, number, number];
export const IDENTITY: M2 = [1, 0, 0, 1, 0, 0];

export function mul(m: M2, n: M2): M2 {
  // returns m * n (apply n first, then m)
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}
export function apply(m: M2, p: Pt): Pt {
  return [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]];
}
export function translate(x: number, y: number): M2 {
  return [1, 0, 0, 1, x, y];
}
export function rotate(rad: number): M2 {
  const c = Math.cos(rad),
    s = Math.sin(rad);
  return [c, s, -s, c, 0, 0];
}
export function scale(sx: number, sy: number): M2 {
  return [sx, 0, 0, sy, 0, 0];
}
// ── OCS (Arbitrary Axis Algorithm) ─────────────────────────────────────────
// Entities like LWPOLYLINE, CIRCLE, ARC, HATCH, INSERT store coords in the
// Object Coordinate System defined by the extrusion vector (210/220/230).
// We only need the XY projection of the OCS→WCS mapping.

function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function norm(a: V3): V3 {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}

/** Returns an M2 that maps OCS xy (+elevation) to WCS xy, or null if extrusion is +Z. */
export function ocsMatrix(ex: V3 | undefined, elevation = 0): M2 | null {
  if (!ex) return null;
  const n = norm(ex);
  if (Math.abs(n[0]) < 1e-12 && Math.abs(n[1]) < 1e-12 && n[2] > 0) return null; // default OCS
  const ax: V3 =
    Math.abs(n[0]) < 1 / 64 && Math.abs(n[1]) < 1 / 64 ? norm(cross([0, 1, 0], n)) : norm(cross([0, 0, 1], n));
  const ay = norm(cross(n, ax));
  // WCS = x*ax + y*ay + z*n ; keep XY
  return [ax[0], ax[1], ay[0], ay[1], elevation * n[0], elevation * n[1]];
}

// ── Flattening ─────────────────────────────────────────────────────────────

/** Max chord deviation (sagitta) in drawing units, set per document. */
export const flat = { tol: 0.01, maxSegAngle: (5 * Math.PI) / 180 };

function segmentsFor(radius: number, sweep: number): number {
  const s = Math.abs(sweep);
  let step = flat.maxSegAngle;
  if (radius > flat.tol) {
    const a = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - flat.tol / radius)));
    if (a > 0 && a < step) step = a;
  }
  return Math.max(2, Math.min(512, Math.ceil(s / step)));
}

/** Points on a circular arc from a0 to a1 (radians, CCW if a1 > a0). Includes both ends. */
function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number): Pt[] {
  const n = segmentsFor(r, a1 - a0);
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return out;
}

/** DXF ARC: angles in degrees, always CCW from start to end. */
export function dxfArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): Pt[] {
  const a0 = (startDeg * Math.PI) / 180;
  let a1 = (endDeg * Math.PI) / 180;
  while (a1 <= a0) a1 += 2 * Math.PI; // wrap-around (e.g. 350° → 10°)
  return arcPoints(cx, cy, r, a0, a1);
}

/** Full circle, closed ring without duplicated last point. */
export function circleRing(cx: number, cy: number, r: number): Pt[] {
  const pts = arcPoints(cx, cy, r, 0, 2 * Math.PI);
  pts.pop();
  return pts;
}

/** Bulge segment p0→p1 (bulge = tan(θ/4)); returns intermediate points, excluding p0, including p1. */
function bulgePoints(p0: Pt, p1: Pt, bulge: number): Pt[] {
  if (Math.abs(bulge) < 1e-12) return [p1];
  const dx = p1[0] - p0[0],
    dy = p1[1] - p0[1];
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-12) return [p1];
  const theta = 4 * Math.atan(bulge); // signed included angle, >0 = CCW
  const r = (chord * (1 + bulge * bulge)) / (4 * Math.abs(bulge));
  // centre = chord midpoint + left normal * (chord/2) * (1 - b²) / (2b)
  const mx = (p0[0] + p1[0]) / 2,
    my = (p0[1] + p1[1]) / 2;
  const nx = -dy / chord,
    ny = dx / chord;
  const off = ((chord / 2) * (1 - bulge * bulge)) / (2 * bulge);
  const cx = mx + nx * off;
  const cy = my + ny * off;
  const a0 = Math.atan2(p0[1] - cy, p0[0] - cx);
  const pts = arcPoints(cx, cy, r, a0, a0 + theta);
  pts[pts.length - 1] = p1; // exact end
  return pts.slice(1);
}

/** Expand a vertex list with bulges into a flattened polyline. bulges[i] belongs to segment i→i+1. */
export function flattenBulgePolyline(pts: Pt[], bulges: number[], closed: boolean): Pt[] {
  if (pts.length === 0) return [];
  const out: Pt[] = [pts[0]];
  const n = pts.length;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const a = pts[i],
      b = pts[(i + 1) % n];
    const more = bulgePoints(a, b, bulges[i] || 0);
    out.push(...more);
  }
  if (closed && out.length > 1) out.pop(); // last point == first
  return out;
}

/** Ellipse (WCS centre, major axis vector, ratio, params in radians). extrusionZ<0 flips minor axis. */
export function ellipsePoints(c: Pt, major: Pt, ratio: number, p0: number, p1: number, extrusionZ = 1): Pt[] {
  while (p1 <= p0) p1 += 2 * Math.PI;
  const a = Math.hypot(major[0], major[1]);
  const minor: Pt = extrusionZ >= 0 ? [-major[1] * ratio, major[0] * ratio] : [major[1] * ratio, -major[0] * ratio];
  const n = segmentsFor(a, p1 - p0);
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = p0 + ((p1 - p0) * i) / n;
    const ct = Math.cos(t),
      st = Math.sin(t);
    out.push([c[0] + major[0] * ct + minor[0] * st, c[1] + major[1] * ct + minor[1] * st]);
  }
  return out;
}

/** Rational B-spline evaluation (de Boor) on a uniform parameter sampling. */
export function nurbsPoints(
  degree: number,
  knots: number[],
  ctrl: Pt[],
  weights: number[] | null,
  samplesPerSpan = 24,
): Pt[] {
  const n = ctrl.length;
  if (n < 2) return ctrl.slice();
  const p = Math.min(degree, n - 1);
  if (knots.length !== n + p + 1) {
    // clamped uniform fallback
    knots = [];
    for (let i = 0; i <= p; i++) knots.push(0);
    for (let i = 1; i < n - p; i++) knots.push(i);
    for (let i = 0; i <= p; i++) knots.push(n - p);
  }
  const w = weights && weights.length === n ? weights : null;
  const t0 = knots[p],
    t1 = knots[n];
  const spans = Math.max(1, n - p);
  const total = spans * samplesPerSpan;
  const out: Pt[] = [];
  for (let s = 0; s <= total; s++) {
    let t = t0 + ((t1 - t0) * s) / total;
    if (s === total) t = t1 - 1e-12 * Math.max(1, Math.abs(t1));
    // find span k with knots[k] <= t < knots[k+1]
    let k = p;
    while (k < n - 1 && t >= knots[k + 1]) k++;
    // homogeneous control points
    const d: [number, number, number][] = [];
    for (let j = 0; j <= p; j++) {
      const i = k - p + j;
      const wi = w ? w[i] : 1;
      d.push([ctrl[i][0] * wi, ctrl[i][1] * wi, wi]);
    }
    for (let r = 1; r <= p; r++) {
      for (let j = p; j >= r; j--) {
        const i = k - p + j;
        const denom = knots[i + p - r + 1] - knots[i];
        const alpha = denom === 0 ? 0 : (t - knots[i]) / denom;
        d[j] = [
          (1 - alpha) * d[j - 1][0] + alpha * d[j][0],
          (1 - alpha) * d[j - 1][1] + alpha * d[j][1],
          (1 - alpha) * d[j - 1][2] + alpha * d[j][2],
        ];
      }
    }
    const q = d[p];
    out.push([q[0] / q[2], q[1] / q[2]]);
  }
  return out;
}

// ── Polygon utilities ──────────────────────────────────────────────────────

export function signedArea(pts: Pt[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j][0] - pts[i][0]) * (pts[j][1] + pts[i][1]);
  return a / 2;
}

export function bbox(pts: Pt[]): [number, number, number, number] {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const [x, y] of pts) {
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  return [x0, y0, x1, y1];
}

export function pointInRing(p: Pt, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0],
      yi = ring[i][1],
      xj = ring[j][0],
      yj = ring[j][1];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Drop consecutive duplicates and a trailing point equal to the first. */
export function cleanRing(pts: Pt[], eps = 1e-9): Pt[] {
  const out: Pt[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (!q || Math.abs(q[0] - p[0]) > eps || Math.abs(q[1] - p[1]) > eps) out.push(p);
  }
  while (out.length > 1) {
    const a = out[0],
      b = out[out.length - 1];
    if (Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps) out.pop();
    else break;
  }
  return out;
}
